import { Alert, Stack, Tooltip, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import React, { useEffect, useState, type ReactNode } from 'react'

import { ChipListMultiValue } from 'components/common/chips/ChipList'
import { ChipWithTooltip } from 'components/common/chips/ChipWithTooltip'
import Icon from 'components/Icon'
import { ObservabilityToolEditor } from 'components/status-checks/common/ObservabilityToolEditor'
import { EditTagsStatusCheckTagsDrawer } from 'components/status-checks/edit/EditTagsStatusCheckTagsDrawer'
import { PNI_AGENT_MATCH_MESSAGES } from 'components/status-checks/edit/pni-agents-matching-tags-utils'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { usePostIntegrationClientsMatchesTags } from 'queries/integration-clients/use-post-integration-clients-matches-tags'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

export function ObservabilityToolDetails({
  header,
  statusCheck,
  tagsFromMonitor
}: {
  header?: ReactNode
  statusCheck: ServiceStatusCheckFromStatusCheck
  tagsFromMonitor?: string[]
}) {
  const { multiPniRouting: isMultiPniRoutingEnabled } = useFeatureFlags()

  const { integration, multiSelectTags } = statusCheck

  const isClone = !statusCheck.id
  const canEditTags = isMultiPniRoutingEnabled && !isClone

  const isPNI = integration.isPrivateNetworkIntegration

  const { mutate: checkMatchingTagsMutation, error: errorCheckMatchingTags } =
    usePostIntegrationClientsMatchesTags()

  const [pniAgentsMatchMessage, setPniAgentsMatchMessage] = useState<string>()

  useEffect(() => {
    if (!isPNI || !multiSelectTags || isEmpty(multiSelectTags)) {
      setPniAgentsMatchMessage(undefined)
      return
    }

    checkMatchingTagsMutation(multiSelectTags, {
      onSuccess(data) {
        setPniAgentsMatchMessage(
          data.matchingAgents.length === 0
            ? PNI_AGENT_MATCH_MESSAGES.NoMatches
            : undefined
        )
      }
    })
  }, [
    checkMatchingTagsMutation,
    isPNI,
    multiSelectTags,
    setPniAgentsMatchMessage
  ])

  return (
    <Stack sx={{ gap: 2, paddingBottom: 1 }}>
      <ObservabilityToolEditor integration={statusCheck.integration} />
      {/* Header: name, monitor link, etc */}
      {header}
      {/* Tags */}
      {tagsFromMonitor && (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}
        >
          <Typography>Tags from Observability Tool:</Typography>
          {tagsFromMonitor.map((tag) => (
            <ChipWithTooltip
              key={tag}
              label={tag}
              tooltipProps={{ title: tag }}
            />
          ))}
        </Stack>
      )}
      {/* Status Check Tags and related PNI alerts */}
      {isMultiPniRoutingEnabled && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}
          >
            <Typography>Tags:</Typography>
            {statusCheck.multiSelectTags && (
              <ChipListMultiValue tags={statusCheck.multiSelectTags} />
            )}
            {canEditTags && (
              <EditTagsStatusCheckTagsDrawer
                monitorTags={
                  // Note: we may pass in monitor tags in the future when they can be used by Gremlin
                  undefined
                }
                statusCheck={statusCheck}
              />
            )}
            {isClone && (
              <Tooltip title="Tags can be edited once health check is saved">
                <Icon icon="solidCircleInfo" />
              </Tooltip>
            )}
          </Stack>
          {
            /* Silently fail because users don't need to be made aware of the error on this page.
            Error details will show up in the edit tags drawer. EN-10063 */
            !errorCheckMatchingTags && pniAgentsMatchMessage && (
              <Alert severity="warning">{pniAgentsMatchMessage}</Alert>
            )
          }
        </>
      )}
    </Stack>
  )
}
